// frontend/src/pages/ProductDetailPage.jsx
import React from 'react';
import { useParams } from 'react-router-dom';
import { Box, Divider, Typography } from '@mui/material';
import { ThemeProvider } from '@mui/material/styles';

import NavBar from '../components/NavBar';
import FilterBar from '../components/FilterBar';
import theme from '../theme';

import { useDashboardData } from '../context/DashboardDataContext';
import EntityHeader from '../components/detail/EntityHeader';
import LockedScopeBanner from '../components/detail/LockedScopeBanner';
import { unslugify, slugify } from '../utils/slug';

import ProductInterviewsTable from '../components/detail/tables/ProductInterviewsTable';
import ProductOnlyTile from '../components/tiles/ProductOnlyTile';
import RelatedJobsForProduct from '../components/detail/RelatedJobsForProduct';
import RelatedSiblingProducts from '../components/detail/RelatedSiblingProducts';

/* ------------------------------------------------------------
   Helpers
------------------------------------------------------------ */

// Scan a job node for product mentions (array + direct fields)
function* iterateProductMentionsInJob(jobNode) {
  if (!jobNode || typeof jobNode !== 'object') return;

  // Direct fields on the job node
  if (jobNode.solution_choice_category) {
    yield {
      product: jobNode.solution_choice_category,
      satisfaction_level: jobNode.satisfaction_level,
      satisfaction_level_quote: jobNode.satisfaction_level_quote,
      satisfaction_level_justification: jobNode.satisfaction_level_justification,
      positives: Array.isArray(jobNode.positives_of_solution_choice) ? jobNode.positives_of_solution_choice : [],
      drawbacks: Array.isArray(jobNode.drawbacks_of_solution_choice) ? jobNode.drawbacks_of_solution_choice : [],
      continueSituations: Array.isArray(jobNode.current_solution_still_used_in_these_situations) ? jobNode.current_solution_still_used_in_these_situations : [],
      stopSituations: Array.isArray(jobNode.not_used_in_these_situations) ? jobNode.not_used_in_these_situations : [],
      raw: jobNode,
    };
  }

  // Common arrays we’ve seen in different exports
  const arraysToCheck = [
    'chosen_solutions',
    'solutions_chosen',
    'products_used',
    'solutions',
    'chosen_products',
  ];

  for (const key of arraysToCheck) {
    const arr = jobNode[key];
    if (!Array.isArray(arr)) continue;
    for (const it of arr) {
      if (!it || typeof it !== 'object') continue;
      if (!it.solution_choice_category) continue;
      yield {
        product: it.solution_choice_category,
        satisfaction_level: it.satisfaction_level,
        satisfaction_level_quote: it.satisfaction_level_quote,
        satisfaction_level_justification: it.satisfaction_level_justification,
        positives: Array.isArray(it.positives_of_solution_choice) ? it.positives_of_solution_choice : [],
        drawbacks: Array.isArray(it.drawbacks_of_solution_choice) ? it.drawbacks_of_solution_choice : [],
        continueSituations: Array.isArray(it.current_solution_still_used_in_these_situations) ? it.current_solution_still_used_in_these_situations : [],
        stopSituations: Array.isArray(it.not_used_in_these_situations) ? it.not_used_in_these_situations : [],
        raw: it,
      };
    }
  }
}

// Collect mentions of the target product across stories (story-level + job-level), de-duplicated per interview
function collectProductMentions(customerStories = [], productName) {
  const target = slugify(productName || '');
  const hits = [];
  const seen = new Set(); // interview-level dedupe (name::date)

  for (const s of customerStories) {
    const key = `${s?.customer_name || ''}::${s?.conversation_date || ''}`;

    // Story-level (your JSON example)
    if (s?.solution_choice_category && slugify(s.solution_choice_category) === target) {
      if (!seen.has(key)) {
        seen.add(key);
        hits.push({
          story: s,
          jobNode: null,
          product: s.solution_choice_category,
          satisfaction_level: s.satisfaction_level,
          satisfaction_level_quote: s.satisfaction_level_quote,
          satisfaction_level_justification: s.satisfaction_level_justification,
          positives: Array.isArray(s.positives_of_solution_choice) ? s.positives_of_solution_choice : [],
          drawbacks: Array.isArray(s.drawbacks_of_solution_choice) ? s.drawbacks_of_solution_choice : [],
          continueSituations: Array.isArray(s.current_solution_still_used_in_these_situations) ? s.current_solution_still_used_in_these_situations : [],
          stopSituations: Array.isArray(s.not_used_in_these_situations) ? s.not_used_in_these_situations : [],
          raw: s,
        });
      }
    }

    // Job-level
    const jobs = Array.isArray(s?.jobs_to_be_done) ? s.jobs_to_be_done : [];
    for (const j of jobs) {
      for (const m of iterateProductMentionsInJob(j)) {
        if (slugify(m.product || '') === target) {
          if (!seen.has(key)) {
            seen.add(key);
            hits.push({
              story: s,
              jobNode: j,
              ...m,
            });
          }
        }
      }
    }
  }
  return hits;
}

// Build header summary (mode satisfaction + latest quote/justification) — used only to feed receipts to the tile header.
function summarizeHeader(mentions) {
  const count = mentions.length;

  // mode satisfaction_level (handles number or string)
  const freq = new Map();
  for (const m of mentions) {
    const raw = m?.satisfaction_level;
    let key;
    if (raw == null) key = '—';
    else if (typeof raw === 'number') key = raw;
    else if (typeof raw === 'string') {
      const s = raw.trim();
      const n = Number(s);
      key = Number.isFinite(n) ? n : (s || '—');
    } else key = '—';
    freq.set(key, (freq.get(key) || 0) + 1);
  }
  const satisfaction_level = [...freq.entries()].sort((a, b) => b[1] - a[1])[0]?.[0] ?? '—';

  // pick most recent quote/justification
  let chosenQuote = '';
  let chosenJust = '';
  let newestTs = -1;

  const parseDate = (d) => {
    if (!d) return 0;
    const ts = Date.parse(d);
    return Number.isNaN(ts) ? 0 : ts;
  };

  for (const m of mentions) {
    const ts = parseDate(m.story?.conversation_date);
    const q = m.satisfaction_level_quote || '';
    const j = m.satisfaction_level_justification || '';
    if ((q || j) && ts >= newestTs) {
      newestTs = ts;
      chosenQuote = q;
      chosenJust = j;
    }
  }

  const receipts = mentions
    .map((m) => m.satisfaction_level_quote)
    .filter(Boolean)
    .slice(0, 6);

  return {
    count,
    satisfaction_level,
    satisfaction_level_quote: chosenQuote,
    satisfaction_level_justification: chosenJust,
    receipts,
  };
}

/* ------------------------------------------------------------
   Component
------------------------------------------------------------ */

export default function ProductDetailPage() {
  const { solutionSlug } = useParams();
  const productName = React.useMemo(() => unslugify(solutionSlug || ''), [solutionSlug]);

  const { ready, customerStories } = useDashboardData();
  const [showReceipts, setShowReceipts] = React.useState(false);

  // Compute once data is ready
  const mentions = React.useMemo(
    () => (ready && productName ? collectProductMentions(customerStories || [], productName) : []),
    [ready, customerStories, productName]
  );

  const sectionTotal = React.useMemo(
    () => (ready ? (customerStories?.length || 0) : 0),
    [ready, customerStories]
  );

  // Header helper just to grab receipts (tile computes the rest)
  const header = React.useMemo(
    () => (ready ? summarizeHeader(mentions) : { count: 0, satisfaction_level: '—', receipts: [] }),
    [ready, mentions]
  );

  // Props for ProductOnlyTile — keep it minimal: the tile will aggregate good/bad + satisfaction
  const headerTileProps = React.useMemo(
    () => ({
      title: productName,
      mentions,        // <-- tile aggregates “good/bad” and satisfaction from this
      sectionTotal,
      receipts: header.receipts,
    }),
    [productName, mentions, sectionTotal, header.receipts]
  );

  return (
    <ThemeProvider theme={theme}>
      <NavBar />
      <Box sx={{ padding: 3 }}>
        <Typography variant="h4" gutterBottom>
          Chosen Solutions
        </Typography>

        <FilterBar />

        {!productName && (
          <Box sx={{ p: 3 }}>
            <Typography variant="h6">Product not found</Typography>
          </Box>
        )}

        {!!productName && (
          <>
            <LockedScopeBanner label="Locked to Product" value={productName} />

            {/* Header tile — ProductOnlyTile computes product-specific metrics */}
            <Box mt={3}>
              <EntityHeader
                title={productName}
                TileComponent={ProductOnlyTile}
                tileProps={headerTileProps}
                onBackPath="/dashboard"
                receiptsEnabled={showReceipts}
                onToggleReceipts={() => setShowReceipts((s) => !s)}
              />
            </Box>

            <Divider sx={{ my: 3 }} />

            {/* Parents: Jobs this product is used for (root-level chosen solution filter) */}
            <RelatedJobsForProduct
              productName={productName}
              customerStories={customerStories || []}
            />

            {/* Siblings: Other chosen solutions used in those same Jobs */}
            <RelatedSiblingProducts
              productName={productName}
              customerStories={customerStories || []}
            />


            {/* Bottom: Interviews table */}
            <Typography variant="h6" sx={{ mt: 4, mb: 1 }}>
              Interviews mentioning this Product
            </Typography>
            <ProductInterviewsTable
              productName={productName}
              customerStories={customerStories || []}
            />
          </>
        )}
      </Box>
    </ThemeProvider>
  );
}
